import { useEffect, useState } from 'react'
import firebase from '../../firebase/compat'

// Eén lopende/afgeronde aanroep per functienaam, gedeeld over de hele sessie.
// Zonder deze cache doet elke wissel van `enabled` — heen en weer klikken tussen
// tabbladen of sectoren, of wegnavigeren en terugkomen — de volledige
// collectiescan aan de serverkant opnieuw. Bij een fout wist de cache zichzelf,
// zodat een volgende poging het wél weer probeert.
// Zelfde recept als src/helpers/meetstandaard/fetchStandaardCatalogs.js.
const inFlight = new Map()

const fetchCounts = (functionName) => {
    if (!inFlight.has(functionName)) {
        inFlight.set(
            functionName,
            firebase.app().functions('europe-west1').httpsCallable(functionName)()
                .catch((error) => {
                    inFlight.delete(functionName)
                    throw error
                })
        )
    }
    return inFlight.get(functionName)
}

/**
 * Haalt per organisatie voorberekende tellingen op voor het projectenoverzicht,
 * via een Cloud Function die een kaart `{ byCompagnyId: {...} }` teruggeeft.
 *
 * Gebruikt door twee aanroepen op dezelfde pagina: `getTocOverview` (Theory of
 * Change) en `getSectorActivityOverview` (registraties per sector). Eén hook,
 * zodat de laad- en foutafhandeling niet in twee kopieën uit elkaar loopt.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * Dit loopt via Cloud Functions en niet via Firestore-hooks, omdat de
 * beheertenant de onderliggende collecties van andere organisaties niet mag
 * lezen. Een geweigerde read in de browser geeft een lege lijst en geen fout,
 * dus alle kolommen zouden stil op 0 staan.
 *
 * De aanroep wordt per functienaam gedeeld en onthouden, zodat heen-en-weer
 * klikken de serverkant niet elke keer opnieuw laat rekenen.
 *
 * `countsByCompagnyId` is `null` zolang er geen antwoord is — dat is iets
 * anders dan een lege kaart. Zonder dat onderscheid zou de tabel tijdens het
 * laden en na een fout hetzelfde beweren als bij een organisatie zonder
 * gegevens: nul.
 *
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @param {string} functionName - naam van de callable, bijv. 'getTocOverview'
 * @param {boolean} enabled - alleen ophalen wanneer de kolommen ook getoond worden
 * @returns {{countsByCompagnyId: Record<string, object> | null, loading: boolean, error: string | null}}
 */
const useOverviewCounts = (functionName, enabled) => {

    const [countsByCompagnyId, setCountsByCompagnyId] = useState(/** @type {Record<string, object> | null} */ (null))
    const [loading, setLoading] = useState(Boolean(enabled))
    const [error, setError] = useState(/** @type {string | null} */ (null))

    useEffect(() => {

        if (!enabled) {
            // Ook de fout wissen: anders blijft de melding boven de tabel staan
            // nadat de kolommen waar hij over ging al weg zijn, en er is geen
            // knop om hem weg te klikken.
            setError(null)
            setLoading(false)
            return undefined
        }

        let cancelled = false
        setLoading(true)
        setError(null)

        fetchCounts(functionName)
            .then((response) => {
                if (cancelled) return
                setCountsByCompagnyId(response?.data?.byCompagnyId || {})
                setLoading(false)
            })
            .catch((err) => {
                if (cancelled) return
                // eslint-disable-next-line no-console
                console.error(`useOverviewCounts: ${functionName} failed`, err)
                setCountsByCompagnyId(null)
                setError(err.message || 'Onbekende fout')
                setLoading(false)
            })

        return () => { cancelled = true }

    }, [functionName, enabled])

    return { countsByCompagnyId, loading, error }
}

export default useOverviewCounts
